import { forwardRef, useImperativeHandle, useEffect } from "react";
import { useSearchParams } from "react-router";
import { Input } from "./input";
import { useGoogleAddress } from "@/hooks/use-google-autocomplete";
import { renderHighlightedText } from "@/utils/highlight-matches";
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";
import { MapPinIcon } from "@heroicons/react/24/outline";

export const GoogleInput = forwardRef(
    ({ onChange, setValue, ...props }, ref) => {
        const {
            inputValue,
            handleInputChange,
            suggestions,
            handleSelect,
            applyPosition,
            handleCurrentLocation,
            clear,
        } = useGoogleAddress(setValue);

        useImperativeHandle(ref, () => ({ clear }));

        let [searchParams] = useSearchParams();

        let latitude = searchParams.get("latitude");
        let longitude = searchParams.get("longitude");

        useEffect(() => {
            if (latitude && longitude) {
                applyPosition(Number(latitude), Number(longitude));
            }
        }, [latitude, longitude, applyPosition]);

        return (
            <Combobox
                value={inputValue}
                onChange={(suggestion) => {
                    if (suggestion === "current-location") {
                        handleCurrentLocation();
                    } else if (suggestion) {
                        handleSelect(suggestion);
                    } else {
                        clear();
                    }
                    onChange?.(suggestion);
                }}
                immediate
            >
                <ComboboxInput
                    as={Input}
                    displayValue={() => inputValue}
                    {...props}
                    onChange={(e) => handleInputChange(e.target.value)}
                />
                <ComboboxOptions
                    anchor="bottom start"
                    transition
                    className="z-100000 mb-3 w-(--input-width) max-w-128! overflow-y-auto rounded-xl bg-white shadow-sm outline-2 outline-gray-200 transition duration-200 ease-out [--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(1)] empty:invisible data-leave:data-closed:opacity-0 md:max-h-128! [&::-webkit-scrollbar]:[width:6px] [&::-webkit-scrollbar-thumb]:[border-radius:3px] [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:[margin:8px_0] [&>div]:mx-1.5 [&>div]:first:mt-1.5 [&>div]:last:mb-1.5"
                >
                    <ComboboxOption
                        value="current-location"
                        className="-mx-1.5 mb-1 flex cursor-pointer items-center rounded-lg px-2 py-1.5 text-sm text-neutral-500 data-[focus]:bg-black/5"
                    >
                        <MapPinIcon className="me-1 size-4 stroke-2 text-green-700" />{" "}
                        Use current location
                    </ComboboxOption>
                    {suggestions.map((s) => (
                        <ComboboxOption
                            key={s.placePrediction.placeId}
                            value={s}
                            className="-mx-1.5 cursor-pointer rounded-lg px-2 py-1.5 text-sm data-[focus]:bg-black/5"
                        >
                            {renderHighlightedText(s.placePrediction.text)}
                        </ComboboxOption>
                    ))}
                </ComboboxOptions>
            </Combobox>
        );
    },
);
